import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { queryOptions, useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ArrowLeft, BookOpen, Info, RotateCcw, Share2 } from "lucide-react";
import { formatUsdShort } from "@/lib/payments/currency";
import { ConsultBand } from "@/components/public/consult-band";
import { TabBar } from "@/components/ds/tab-bar";
import { getTraumaResult, markTraumaCta, startTraumaRetake } from "@/lib/trauma/attempts.functions";
import { TRAUMA_RESULT_UI as UI, TRAUMA_BUY_UI as BUY, TRAUMA_RESULT_PAGE_UI as R } from "@/lib/trauma/ui-copy";
import { useTraumaPurchaseState } from "@/lib/trauma/purchase-query";
import { traumaShareMessage } from "@/lib/trauma/result-ui";
import { toWhatsAppShareLink } from "@/lib/whatsapp-link";
import { absoluteUrl } from "@/lib/seo";
import { arabicDate } from "@/lib/arabic-format";
import { AxisTile, TraumaChip, TraumaProgress, TraumaTopBar } from "@/components/trauma/chrome";
import { AnswerCell, answerCat } from "@/components/trauma/answer-cell";
import { EASE_OUT, canAnimate, prefersReducedMotion, settleChildren, useIsoLayoutEffect } from "@/lib/trauma/motion";
import { useHydrated } from "@tanstack/react-router";
import { cn } from "@/lib/utils";

const resultQuery = (attemptId: string) => queryOptions({
  queryKey: ["trauma", "result", attemptId],
  queryFn: () => getTraumaResult({ data: { attemptId } }),
  gcTime: 0,
  retry: false,
});

export const Route = createFileRoute("/_authenticated/courses/trauma/$id/result/$attemptId")({
  loader: async ({ context, params }) => {
    try {
      const result = await context.queryClient.ensureQueryData(resultQuery(params.attemptId));
      if (result.courseId !== params.id) throw new Error("COURSE_MISMATCH");
      return result;
    } catch { throw redirect({ to: "/" }); }
  },
  gcTime: 0,
  head: () => ({ meta: [
    { title: `${UI.resultTitle} — د. سارة النحّاس` },
    { name: "description", content: "نتيجة خاصة لدورة صدمات الطفولة من د. سارة النحّاس." },
    { property: "og:title", content: `${UI.resultTitle} — د. سارة النحّاس` },
    { property: "og:description", content: "نتيجة خاصة لدورة صدمات الطفولة." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
    { name: "robots", content: "noindex" },
  ] }),
  errorComponent: () => <Link to="/portal/courses">{UI.myCourses}</Link>,
  notFoundComponent: () => <Link to="/portal/courses">{UI.myCourses}</Link>,
  component: ResultPage,
});

const focusRing = "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-amber-500/50";
const secondaryPill =
  `inline-flex min-h-12 items-center justify-center gap-2 rounded-ds-pill border border-hairline-default bg-surface px-6 font-display text-[16px] font-bold text-brown-700 transition-colors duration-ds-fast hover:bg-cream-50 disabled:opacity-60 [&_svg]:size-[18px] ${focusRing}`;
const ghostPill =
  `inline-flex min-h-12 items-center justify-center gap-2 rounded-ds-pill px-5 font-display text-[16px] font-bold text-brown-700 transition-colors duration-ds-fast hover:bg-cream-100 disabled:opacity-60 [&_svg]:size-[18px] ${focusRing}`;
const h2 = "font-display text-[23px] font-extrabold leading-snug text-brown-700";
const sectionCls = "mt-11 border-t border-hairline-subtle pt-11";

/** Counts up from 0 over 700ms after a 420ms lead when animating; otherwise shows the final value. */
function useCountUp(to: number, animate: boolean) {
  const [v, setV] = useState(animate ? 0 : to);
  useIsoLayoutEffect(() => {
    if (!animate) return setV(to);
    setV(0);
    let raf = 0;
    const t0 = performance.now() + 420;
    const tick = (now: number) => {
      const p = Math.min(1, Math.max(0, (now - t0) / 700));
      setV(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, animate]);
  return v;
}

function CountUp({ to, animate }: { to: number; animate: boolean }) {
  return <>{useCountUp(to, animate)}</>;
}

function ResultPage() {
  const initialData = Route.useLoaderData();
  const { id, attemptId } = Route.useParams();
  const { data: result } = useSuspenseQuery({ ...resultQuery(attemptId), initialData });
  const { offers } = useTraumaPurchaseState();
  const nextSlug = `trauma-${result.nextCourse.id}`;
  const nextOffer = offers?.[nextSlug];
  const navigate = Route.useNavigate();
  const mark = useServerFn(markTraumaCta);
  const retake = useServerFn(startTraumaRetake);
  const cta = useMutation({ mutationFn: (kind: "book" | "next" | "share") => mark({ data: { attemptId, kind } }) });
  const restart = useMutation({
    mutationFn: () => retake({ data: { courseId: id } }),
    onSuccess: () => navigate({ to: "/courses/trauma/$id/play", params: { id } }),
    onError: () => toast.error(UI.retryError),
  });
  const shareLink = toWhatsAppShareLink(traumaShareMessage(result.title, result.definition, absoluteUrl(`/courses/trauma/${id}`)));
  const { total, counts, axes, questions } = result;
  const toReview = 16 - counts.safe;
  const [filter, setFilter] = useState<"review" | "all">(toReview > 0 ? "review" : "all");
  const [scrollTo, setScrollTo] = useState<number | null>(null);
  const hlSet = new Set(result.highlights.map((h) => h.axisN));
  const strong = axes.filter((a) => a.score === 0);
  const axisLabel = (n: number) => axes.find((a) => a.n === n)?.label ?? "";
  const safePct = (counts.safe / 16) * 100;

  const rootRef = useRef<HTMLDivElement>(null);
  const stackRef = useRef<HTMLDivElement>(null);
  // Decided once per mount: only in-app navigation (already hydrated) animates; SSR/hard refresh and reduced motion show the page at rest.
  const hydrated = useHydrated();
  const [animate] = useState(() => hydrated && !prefersReducedMotion());
  const [fill, setFill] = useState(animate ? 0 : safePct);

  // The one orchestrated moment: the progress fills, the stack grows, numbers count up.
  useIsoLayoutEffect(() => {
    if (!animate) return setFill(safePct);
    if (rootRef.current) settleChildren(rootRef.current);
    const t = window.setTimeout(() => setFill(safePct), 380);
    stackRef.current?.querySelectorAll("i").forEach((seg, k) => {
      if (canAnimate(seg)) seg.animate([{ transform: "scaleX(0)" }, { transform: "scaleX(1)" }], { duration: 600, delay: 450 + k * 140, easing: EASE_OUT, fill: "backwards" });
    });
    return () => clearTimeout(t);
  }, [safePct, animate]);

  useEffect(() => {
    if (scrollTo === null) return;
    document.getElementById(`rv-axis-${scrollTo}`)?.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
    setScrollTo(null);
  }, [scrollTo]);

  const visibleQs = questions.filter((q) => filter === "all" || q.picked.score !== 0);
  const firstOfAxis = new Set<number>();

  const nextCta = result.nextCourse.canOpen
    ? { to: "/courses/trauma/$id/play" as const, params: { id: result.nextCourse.id }, label: UI.start, price: null }
    : nextOffer?.canBuy
      ? { to: "/courses/trauma/buy/$productSlug" as const, params: { productSlug: nextSlug }, label: BUY.buyCourse, price: nextOffer.price_usd }
      : { to: "/courses/trauma/$id" as const, params: { id: result.nextCourse.id }, label: UI.discover, price: null };

  return (
    <main dir="rtl" className="min-h-dvh bg-cream-200 font-body text-[17px] leading-[1.85] text-brown-700 [&_h1]:tracking-normal [&_h2]:tracking-normal [&_h3]:tracking-normal">
      <TraumaTopBar mode="back" title={result.title} />
      <div ref={rootRef} className="mx-auto max-w-[44rem] px-5 pt-8 pb-16">
        {/* Hero */}
        <p className="font-display text-[14px] font-bold text-amber-800">{R.eyebrow}</p>
        <h1 className="mt-2 font-display text-[34px] leading-[1.3] font-extrabold">{result.level.title}</h1>
        {result.previousLevel ? (
          <TraumaChip className="mt-3">{UI.previous}: {result.previousLevel.title}
            {result.previousLevel.completed_at ? <> · <time dateTime={result.previousLevel.completed_at}>{arabicDate(result.previousLevel.completed_at)}</time></> : null}
          </TraumaChip>
        ) : null}

        <section data-settle aria-label={R.gaugeAria(total, result.level.title)} className="-mx-2 mt-6 rounded-ds-2xl bg-green-100 px-6 py-8 sm:mx-0 lg:px-10">
          <p className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-display text-[44px] leading-none font-extrabold text-green-800 tabular-nums" dir="ltr"><CountUp to={counts.safe} animate={animate} /></span>
            <span className="font-display text-[19px] font-bold text-green-800">{R.ofSixteen}</span>
          </p>
          <p className="mt-2 font-display text-[17px] font-bold">{R.safeHeadline}</p>
          <div className="mt-4"><TraumaProgress pct={fill} label={R.safeProgress} /></div>
          <p className="mt-3 text-[14px] text-brown-500">{R.gaugeCaption(total)}</p>
          <p className="mt-5 whitespace-pre-line">{result.level.text}</p>
          <div className="mt-5 border-t border-green-800/15 pt-5">
            <h2 className="font-display text-[17px] font-bold">{UI.firstStep}</h2>
            <p className="mt-1 whitespace-pre-line">{result.level.first_step}</p>
          </div>
        </section>

        {/* Numbers */}
        <section data-settle className={sectionCls} aria-labelledby="r-numbers">
          <h2 id="r-numbers" className={h2}>{R.numbersTitle}</h2>
          <div ref={stackRef} role="img" aria-label={R.stackAria(counts.safe, counts.grey, counts.attention)} className="mt-5 flex h-3 gap-0.5">
            {[counts.safe, counts.grey, counts.attention].map((c, s) =>
              c ? <i key={s} style={{ flex: c }} className={cn("block h-full origin-right rounded-sm first:rounded-s-full last:rounded-e-full", answerCat(s).fill)} /> : null,
            )}
          </div>
          <ul className="mt-4">
            {[counts.safe, counts.grey, counts.attention].map((c, s) => (
              <li key={s} className="flex min-h-12 items-center gap-3 border-b border-hairline-subtle py-2 last:border-b-0">
                <AnswerCell score={s} size="md" />
                <span className="min-w-0 flex-1 font-display text-[15px] font-bold">{answerCat(s).label}</span>
                <span className="font-display text-[23px] font-extrabold tabular-nums" dir="ltr">
                  <CountUp to={c} animate={animate} /><small className="text-[14px] font-bold text-brown-500"> {R.outOf16}</small>
                </span>
              </li>
            ))}
          </ul>
        </section>

        {/* Axis map */}
        <section data-settle className={sectionCls} aria-labelledby="r-map">
          <h2 id="r-map" className={h2}>{R.axesMap}</h2>
          <p className="mt-1 text-[14px] text-brown-500">{R.axesMapSub}</p>
          <div className="mt-4">
            {axes.map((a) => {
              const hl = hlSet.has(a.n);
              return (
                <button
                  key={a.n}
                  type="button"
                  aria-label={`${R.axisN(a.n)}: ${a.label}. ${a.answerScores.map((x) => answerCat(x).label).join("، ")}`}
                  onClick={() => { setFilter("all"); setScrollTo(a.n); }}
                  className={cn("grid min-h-12 w-full grid-cols-[40px_minmax(0,1fr)_auto] items-center gap-3 border-b border-hairline-subtle py-3 text-start transition-colors duration-ds-fast last:border-b-0 hover:bg-cream-100", focusRing)}
                >
                  <AxisTile n={a.n} size={40} />
                  <span className="min-w-0">
                    <small className="font-display text-[13px] font-bold text-amber-800">{R.axisN(a.n)}</small>
                    <b className="block font-display text-[15px] leading-[1.4]">{a.label}</b>
                    {hl ? (
                      <span className="mt-1 inline-block rounded-ds-pill bg-coral-100 px-2.5 py-0.5 font-display text-[13px] font-bold text-coral-700">{R.flagAttention}</span>
                    ) : a.score === 0 ? (
                      <span className="mt-1 inline-block rounded-ds-pill bg-green-100 px-2.5 py-0.5 font-display text-[13px] font-bold text-green-800">{R.flagStrength}</span>
                    ) : null}
                  </span>
                  <span className="flex gap-1">{a.answerScores.map((x, k) => <AnswerCell key={k} score={x} title={answerCat(x).label} />)}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 font-display text-[13px] font-bold text-brown-500">
            {[0, 1, 2].map((s) => <span key={s} className="inline-flex items-center gap-1.5"><AnswerCell score={s} size="xs" />{answerCat(s).label}</span>)}
          </div>
        </section>

        {/* Highlights + strengths */}
        {result.highlights.length || strong.length ? (
          <section data-settle className={sectionCls} aria-labelledby={result.highlights.length ? "r-hl" : undefined}>
            {result.highlights.length ? (
              <>
                <h2 id="r-hl" className={h2}>{R.highlightsHeading(result.highlights.length)}</h2>
                <div className="mt-2">
                  {result.highlights.map((h) => (
                    <article key={h.axisN} className="border-b border-hairline-subtle py-6 last:border-b-0">
                      <div className="flex items-center gap-3">
                        <AxisTile n={h.axisN} size={44} />
                        <div>
                          <small className="font-display text-[13px] font-bold text-amber-800">{R.axisN(h.axisN)}</small>
                          <h3 className="font-display text-[17px] font-bold">{h.label}</h3>
                        </div>
                      </div>
                      <blockquote className="mt-4 rounded-ds-lg bg-cream-100 px-5 py-4 font-display leading-[1.7] font-bold not-italic">{h.sara_line}</blockquote>
                      <Link to="/courses/trauma/$id/lessons" params={{ id }} search={{ r: attemptId }} hash={`axis-${h.axisN}`} className={cn(ghostPill, "mt-3 -ms-3")}>
                        <BookOpen strokeWidth={1.75} aria-hidden />{UI.readLesson} ←
                      </Link>
                    </article>
                  ))}
                </div>
              </>
            ) : null}
            {strong.length ? (
              <div className="mt-4 flex items-center gap-3 rounded-ds-lg bg-green-100 px-5 py-4 text-green-800">
                <AnswerCell score={0} />
                <p>{R.strongPrefix} <b className="font-display">{R.inAxes(strong.length)}</b>: {strong.map((a) => a.label).join("، ")}</p>
              </div>
            ) : null}
          </section>
        ) : null}

        {/* Review */}
        <section data-settle className={sectionCls} aria-labelledby="r-review">
          <h2 id="r-review" className={h2}>{R.reviewTitle}</h2>
          <p className="mt-1 text-[14px] text-brown-500">{R.reviewSub}</p>
          {toReview ? (
            <div className="mt-4 max-w-sm">
              <TabBar
                label={R.filterAria}
                value={filter}
                onChange={setFilter}
                options={[{ value: "review", label: R.filterReview(toReview) }, { value: "all", label: R.filterAll }]}
              />
            </div>
          ) : null}
          <div className="mt-2">
            {visibleQs.map((q) => {
              const c = answerCat(q.picked.score);
              const first = !firstOfAxis.has(q.axisN);
              firstOfAxis.add(q.axisN);
              return (
                <article key={q.n} id={first ? `rv-axis-${q.axisN}` : undefined} className="scroll-mt-20 border-b border-hairline-subtle py-6 last:border-b-0">
                  <p className="font-display text-[13px] font-bold text-amber-800">{R.questionMeta(q.n, axisLabel(q.axisN))}</p>
                  <h3 className="mt-1.5 font-display text-[17px] leading-[1.7] font-bold">{q.scenario}</h3>
                  <div className={cn("mt-3 rounded-ds-lg px-5 py-4", c.soft)}>
                    <p className={cn("flex items-center gap-2 font-display text-[14px] font-bold", c.text)}>
                      <AnswerCell score={q.picked.score} size="sm" />{R.yourAnswer(c.label)}
                    </p>
                    <p className="mt-1.5 text-[17px]">{q.picked.text}</p>
                  </div>
                  {q.picked.score === 0 ? (
                    <p className="mt-3 flex items-center gap-2 font-display text-[14px] font-bold text-green-800">
                      <AnswerCell score={0} size="sm" />{R.pickedSafest}
                    </p>
                  ) : (
                    <div className="mt-3 rounded-ds-lg bg-green-100 px-5 py-4">
                      <p className="flex items-center gap-2 font-display text-[14px] font-bold text-green-800">
                        <AnswerCell score={0} size="sm" />{R.safest}
                      </p>
                      <p className="mt-1.5 text-[17px]">{q.best.text}</p>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>

        {/* What next */}
        <section data-settle className={sectionCls} aria-labelledby="r-next">
          <h2 id="r-next" className={h2}>{UI.whatNext}</h2>
          <div className="-mx-5 mt-5 sm:mx-0 [&>section]:lg:mx-0">
            <ConsultBand
              title={R.bookTitle}
              text={UI.bookLine}
              href={`/book?from=trauma-${id}`}
              onBook={() => cta.mutate("book")}
            />
          </div>
          <Link
            to={nextCta.to}
            params={nextCta.params as never}
            onClick={() => cta.mutate("next")}
            className={cn("mt-6 flex min-h-12 items-center gap-4 border-y border-hairline-subtle py-4 transition-colors duration-ds-fast hover:bg-cream-100", focusRing)}
          >
            <span className="grid size-[52px] shrink-0 place-items-center rounded-ds-md bg-amber-100 text-amber-800"><BookOpen className="size-6" strokeWidth={1.75} aria-hidden /></span>
            <span className="min-w-0 flex-1">
              <small className="font-display text-[13px] font-bold text-amber-800">{R.nextCourseOf(result.nextCourse.order)}</small>
              <b className="block font-display text-[17px] leading-snug">{result.nextCourse.title}</b>
              <span className="mt-0.5 block font-display text-[14px] font-bold text-brown-500">
                {nextCta.label}{nextCta.price != null ? <> · <span dir="ltr">{formatUsdShort(nextCta.price)}</span></> : null}
              </span>
            </span>
            <ArrowLeft className="size-5 shrink-0" strokeWidth={1.75} aria-hidden />
          </Link>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href={shareLink} target="_blank" rel="noopener noreferrer" onClick={() => cta.mutate("share")} className={secondaryPill}>
              <Share2 strokeWidth={1.75} aria-hidden />{UI.share}
            </a>
            <button type="button" className={ghostPill} disabled={restart.isPending} onClick={() => restart.mutate()}>
              <RotateCcw strokeWidth={1.75} aria-hidden />{UI.retake}
            </button>
          </div>
          <p className="mt-8 flex gap-2 text-[14px] leading-[1.8] text-brown-500">
            <Info className="mt-1 size-[18px] shrink-0" strokeWidth={1.75} aria-hidden /><span className="whitespace-pre-line">{result.boundaryNote}</span>
          </p>
        </section>
      </div>
    </main>
  );
}
